import { type HTMLAttributes, type ReactNode } from "react";
import styled, { css } from "styled-components";

import { TRUNCATE_ATTR, TRUNCATE_LINES_ATTR, TRUNCATE_TEXT_ATTR } from "./truncation";

export interface TruncatedTextProps extends HTMLAttributes<HTMLSpanElement> {
  children: ReactNode;
  /** Block-safe wrapper for composite content. Defaults to `span`. */
  as?: "span" | "div";
  /** 1 = 단일 행 ellipsis(기본). >1 = 그 행 수로 다중 행 clamp. */
  lines?: number;
  /**
   * 툴팁에 띄울 전체 값 오버라이드. 기본은 이 요소의 `textContent` — children이 포맷된
   * ReactNode(composite)거나 보이는 텍스트와 다른 정답 문자열이 있을 때만 준다.
   * (네이티브 `title`을 쓰지 않는 이유: 브라우저 툴팁이 이중으로 뜬다.)
   */
  fullText?: string;
}

// ellipsis만 입히고 자신을 마커로 표시한다. 툴팁 래퍼로 감싸지 않는 게 핵심 — 감싸면 flex/grid의
// 폭-제약 사슬이 끊겨 truncation 자체가 깨진다(RFC-truncation-tooltip §아키텍처). 표시는 <TruncationTooltipHost>가
// 이벤트 위임으로 담당하므로 여기는 CSS + 마커만. min-width:0 은 flex 자식에서 축소를 허용해 ellipsis가 걸리게.
const Truncated = styled.span<{ $lines: number }>`
  min-width: 0;
  overflow: hidden;
  ${({ $lines }) =>
    $lines <= 1
      ? css`
          /* block 이라야 부모 폭을 채우고 넘침을 클립한다(inline span은 ellipsis가 안 걸린다).
             flex/grid 부모에선 min-width:0 과 함께 flex 아이템으로 축소돼 잘린다. */
          display: block;
          white-space: nowrap;
          text-overflow: ellipsis;
        `
      : css`
          /* line-clamp: Chromium/Safari/Firefox 지원(c6 주 타깃 Chromium). */
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: ${$lines};
          /* 부모의 nowrap(표/리스트 셀에 흔함)을 상속하면 줄바꿈이 없어 line-clamp가 무의미해진다
             — normal로 되돌려 여러 줄로 감싼 뒤 세로 clamp가 걸리게 한다. */
          white-space: normal;
        `}
`;

/**
 * 텍스트를 ellipsis로 자르고, **실제로 잘렸을 때** hover하면 전체 값을 툴팁으로 복원한다.
 * 툴팁 렌더는 앱 루트의 `<TruncationTooltipHost>`(공유 단일 인스턴스)가 담당한다 — 이 컴포넌트는
 * 표시 자체를 하지 않고 잘림 대상만 표시(marker)한다. 전체 값은 DOM textContent에서 읽으므로
 * (ellipsis는 렌더 전용) 별도 prop이 필요 없다.
 *
 * 복원 버블은 c6 `Tooltip`과 **같은 버블**(시각·타이밍·a11y 공유)로 뜬다 — 부착 모델만 다르다.
 * 용도 설명을 저작할 땐 `Tooltip`(content prop), 잘린 텍스트 복원은 이 컴포넌트/마커.
 * 한 요소에 둘 다 쓰면 이중 버블(Tooltip이 dev에서 경고).
 */
export function TruncatedText({ children, lines = 1, fullText, ...rest }: TruncatedTextProps) {
  const multiline = lines > 1;
  return (
    <Truncated
      {...rest}
      $lines={lines}
      {...{ [TRUNCATE_ATTR]: "" }}
      {...(multiline ? { [TRUNCATE_LINES_ATTR]: String(lines) } : null)}
      {...(fullText != null ? { [TRUNCATE_TEXT_ATTR]: fullText } : null)}
    >
      {children}
    </Truncated>
  );
}

TruncatedText.displayName = "TruncatedText";
